'use client';

import Link from 'next/link';
import React from 'react';
import Image from 'next/image';

import { IResolvedFooter } from '@/src/utils/collections/footer.mapper';
import { FooterSocials } from '@/src/components/footer/FooterSocials';
import { FooterMailTo } from '@/src/components/footer/FooterMailTo';
import { FooterGridLinks } from '@/src/components/footer/FooterGridLinks';
import { IZoneWithDestinationsAndPromo } from '@/src/modules/destinations/destinations.modules';
import { FooterCookieButton } from '@/src/components/footer/FooterCookieButton';
import { DownloadButtons } from '@/src/components/ui/DownloadButtons/DownloadButtons';

type IFooterProps = {
  footer: IResolvedFooter;
  destinations: Record<string, IZoneWithDestinationsAndPromo>;
  locale: string;
};

const makeDestinationUrls = (
  destinations: Record<string, IZoneWithDestinationsAndPromo>,
  locale: string
) => {
  return Object.keys(destinations)
    .slice(0, 5)
    .map(item => ({
      name: item,
      url: `/${locale}/destinations/${item}`,
    }));
};

const Footer = ({ destinations, footer, locale }: IFooterProps) => {
  return (
    <footer className="mt-10 bg-black py-20">
      <div className="mx-auto w-full max-w-1200 px-4">
        <div className="mb-14 space-y-8 md:flex md:justify-between md:space-y-0">
          <div className="space-y-5">
            <Link
              href={`/${locale}`}
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_button',
                  click_id: `/${locale}`,
                  click_label: 'GoMoWorld',
                });
              }}
            >
              <Image
                className="w-[200px} h-[38px]"
                src={'/static/images/logo-red.webp'}
                alt={'GoMoWorld Logo'}
                width={200}
                height={38}
                quality={100}
              />
            </Link>
            <DownloadButtons
              linkToAppStore={footer.linkToAppStore}
              linkToPlayStore={footer.linkToPlayStore}
            />
            <FooterSocials />
            <FooterMailTo />
          </div>
          <div className={'grid gap-10 lg:grid-cols-2 lg:gap-20'}>
            <FooterGridLinks title={'GoMoWorld'} items={footer.mainNavLinks} />
            <FooterGridLinks
              title={footer.mostPopularDestination}
              items={makeDestinationUrls(destinations, locale)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <div className="mb-8 flex flex-col gap-3 text-sm text-light md:flex-row md:items-center lg:mb-0">
            {footer.subNavLinks.map(link => (
              <Link
                className={'text-neutral-300 no-underline'}
                href={link.url}
                key={link.url}
                onClick={() => {
                  window.dataLayer?.push({
                    event: 'click_button',
                    click_id: link.url,
                    click_label: link.name,
                  });
                }}
              >
                {link.name}
              </Link>
            ))}
            <FooterCookieButton text={footer.cookieBanner} />
          </div>
          <div className={'flex flex-col items-center gap-4 lg:flex-row'}>
            <p className={'text-sm text-light'}>{footer.copyright}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
